import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Modal, Button } from '@components/ui';
import CompanySelector from '@components/shared/CompanySelector';
import { izolasyonTipleri } from '@mocks/materials/izolasyon';
import { mockCompanies } from '@mocks/firmalar';
import { calculateKDV, DEFAULT_KDV_RATE } from '@utils/kdv';
import KDVSummary from '../../components/KDVSummary';

/**
 * Props:
 *   onSubmit(data, isEdit) — Normal malzeme sayfasından kullanım
 *   onMaterialSubmit(enrichedData) — Finance modundan açıldığında
 *   submitLabel — Buton yazısı
 */
export default function IzolasyonModal({ isOpen, onClose, onSubmit, onMaterialSubmit, submitLabel, initialData = null, isLoading = false }) {
    const isEdit = !!initialData;
    const [selectedCompany, setSelectedCompany] = useState(
        initialData?.supplierId ? { id: initialData.supplierId, name: initialData.tedarikci } : null
    );

    const { register, handleSubmit, reset, watch, formState: { errors } } = useForm({
        defaultValues: initialData || { siparisTarihi: new Date().toISOString().split('T')[0], tip: 'su', birim: 'm²', durum: 'bekliyor', kdvOrani: DEFAULT_KDV_RATE },
    });

    const watchedBirimFiyat = watch('birimFiyat');
    const watchedMiktar = watch('miktar');
    const watchedKdvOrani = watch('kdvOrani');

    const handleFormSubmit = (data) => {
        const result = calculateKDV({ unitPrice: data.birimFiyat, quantity: data.miktar, vatRate: data.kdvOrani });
        data.toplamFiyat = result.toplamHaric;
        data.kdvOrani = result.kdvOrani;
        data.kdvTutari = result.kdvTutari;
        data.kdvDahilToplam = result.toplamDahil;
        data.kalanMiktar = data.kalanMiktar || data.miktar;
        data.uygulamaYuzde = Math.round(((data.miktar - data.kalanMiktar) / data.miktar) * 100);
        data.tedarikci = selectedCompany?.name || null;
        data.supplierId = selectedCompany?.id || null;

        const enriched = {
            ...data,
            selectedCompany,
            calculatedTotal: data.kdvDahilToplam,
            autoDescription: `İzolasyon Alımı — ${data.miktar} ${data.birim} ${data.tip}`,
        };

        if (onMaterialSubmit) {
            onMaterialSubmit(enriched);
        } else {
            onSubmit(enriched, isEdit);
        }
    };

    const inputClass = "w-full px-3 py-2.5 bg-bg-primary border border-border-light rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50";

    return (
        <Modal isOpen={isOpen} onClose={() => { reset(); onClose(); }} title={isEdit ? 'İzolasyon Düzenle' : 'Yeni İzolasyon'} size="lg"
            footer={<><Button variant="ghost" onClick={() => { reset(); onClose(); }} disabled={isLoading}>İptal</Button><Button variant="primary" onClick={handleSubmit(handleFormSubmit)} isLoading={isLoading}>{submitLabel || (isEdit ? 'Güncelle' : 'Kaydet')}</Button></>}>
            <form className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">İzolasyon Tipi <span className="text-error">*</span></label>
                        <select {...register('tip', { required: true })} className={inputClass}>{izolasyonTipleri.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}</select></div>
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Sipariş Tarihi <span className="text-error">*</span></label>
                        <input type="date" {...register('siparisTarihi', { required: true })} className={inputClass} /></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Marka <span className="text-error">*</span></label>
                        <input type="text" {...register('marka', { required: true })} placeholder="İzolasyon markası" className={`${inputClass} ${errors.marka ? 'border-error' : ''}`} /></div>
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Model/Ürün Adı</label>
                        <input type="text" {...register('model')} placeholder="Model adı" className={inputClass} /></div>
                </div>
                <div>
                    <CompanySelector
                        value={selectedCompany}
                        onChange={setSelectedCompany}
                        companies={mockCompanies}
                        label="Tedarikçi / Firma"
                    />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Miktar <span className="text-error">*</span></label>
                        <input type="number" step="0.1" {...register('miktar', { required: true, valueAsNumber: true })} placeholder="0" className={inputClass} /></div>
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Birim</label>
                        <select {...register('birim')} className={inputClass}><option value="m²">m² (Metrekare)</option><option value="mt">mt (Metre)</option><option value="kg">kg (Kilogram)</option><option value="adet">Adet</option></select></div>
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Birim Fiyat (₺)</label>
                        <input type="number" {...register('birimFiyat', { valueAsNumber: true })} placeholder="0" className={inputClass} /></div>
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">KDV Oranı (%)</label>
                        <input type="number" step="1" {...register('kdvOrani', { valueAsNumber: true })} placeholder="20" className={inputClass} /></div>
                </div>
                <KDVSummary unitPrice={watchedBirimFiyat} quantity={watchedMiktar} vatRate={watchedKdvOrani} />
                <div><label className="block text-sm font-medium text-text-primary mb-1.5">Uygulama Alanı</label>
                    <input type="text" {...register('uygulamaAlani')} placeholder="Örn: A Blok Bodrum" className={inputClass} /></div>
                <div><label className="block text-sm font-medium text-text-primary mb-1.5">Not</label>
                    <textarea {...register('not')} rows={2} placeholder="İzolasyon notu..." className={`${inputClass} resize-none`} /></div>
            </form>
        </Modal>
    );
}
